import { Gift } from 'lucide-react'
import { useEffect, useState } from 'react'

import { BACKSTAGE_EXIT_MS } from './BackstageStrip'
import { CountUp } from './CountUp'
import { StepStatus } from './SetupProgress'

/** Names the job rather than the agent: setup is not the account's own chat. */
export const COMPOSER_LABEL = 'Reply to onboarding assistant'

/** Tracks the conversation on screen: the accounts are connected, so what is
 *  live is the question being asked. */
export const STEPS = [
  { label: 'Confirm your info', status: StepStatus.COMPLETED },
  { label: 'Connect accounts', status: StepStatus.COMPLETED },
  { label: 'CFO preferences', status: StepStatus.IN_PROGRESS },
  { label: 'Payment info', status: StepStatus.LOCKED },
  { label: 'Start free one month trial', status: StepStatus.LOCKED, icon: Gift },
]

/** How far the stepper has actually got, rather than a bar that just moves. */
export const COMPLETE =
  (STEPS.filter((step) => step.status === StepStatus.COMPLETED).length / STEPS.length) * 100

export const CURRENT_STEP = STEPS.findIndex((step) => step.status === StepStatus.IN_PROGRESS)

/** What the last question offers, in place of making them write it out. */
export const PRIORITIES = [
  'Preparing my fleet to grow',
  'Ensuring I’m running as efficiently as possible',
  'Avoiding cash crunches and Friday afternoon surprises',
]

/** What runs after an answer lands, one line at a time. */
export const BACKSTAGE = [
  (active: boolean) => (
    <>
      Scanning transactions… <CountUp to={4812} run={active} />
    </>
  ),
  (active: boolean) => (
    <>
      Matching settlements to loads… <CountUp to={312} run={active} />
    </>
  ),
  () => 'Personalizing your Cai…',
]

/** Room for a slow fade in, the count finishing, and a beat before the swap. */
const BACKSTAGE_MS = 2200

/** Steps the backstage run on every send, so both screens behave identically. */
export function useBackstageRun(sent: number) {
  const [running, setRunning] = useState(-1)
  const [exiting, setExiting] = useState(false)

  useEffect(() => {
    if (sent === 0) return
    setExiting(false)
    setRunning(0)
    const done = BACKSTAGE.length * BACKSTAGE_MS
    const timers = [
      ...BACKSTAGE.slice(1).map((_, index) =>
        window.setTimeout(() => setRunning(index + 1), (index + 1) * BACKSTAGE_MS)
      ),
      // The line has to stay mounted through its exit before being cleared.
      window.setTimeout(() => setExiting(true), done),
      window.setTimeout(() => {
        setRunning(-1)
        setExiting(false)
      }, done + BACKSTAGE_EXIT_MS),
    ]
    return () => timers.forEach(window.clearTimeout)
  }, [sent])

  return { running, exiting }
}
